import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  X, Mail, Building2, Phone, MessageSquare, Clock, Flame,
  FileText, ChevronDown, Trash2, Send, DollarSign, UserCircle,
  Calendar, CheckCircle, AlertCircle, Plus, MailCheck, Pause,
  Play, Square, RefreshCw, CalendarCheck, Zap, Smartphone,
  ArrowUp, ArrowDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ConsentBadge } from '../components/admin/ConsentBadge';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { value: 'new', label: 'New', color: 'bg-slate-500/15 text-slate-300 border-slate-500/30' },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  { value: 'meeting_scheduled', label: 'Meeting Scheduled', color: 'bg-violet-500/15 text-violet-400 border-violet-500/30' },
  { value: 'proposal_sent', label: 'Proposal Sent', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  { value: 'won', label: 'Won', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  { value: 'lost', label: 'Lost', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
];

function getStatusConfig(status) { return STATUSES.find(s => s.value === status) || STATUSES[0]; }
function isHotLead(ca) { return (Date.now() - new Date(ca).getTime()) < 86400000; }
function timeAgo(ds) {
  const m = Math.floor((Date.now() - new Date(ds).getTime()) / 60000);
  if (m < 1) return 'just now'; if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
function getAuthHeaders() { return { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }; }
function isOverdue(dd) { return dd && dd.slice(0, 10) < new Date().toISOString().slice(0, 10); }
function isDueToday(dd) { return dd && dd.slice(0, 10) === new Date().toISOString().slice(0, 10); }

export default function LeadDetail({ leadId, onClose, onUpdate }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [editValue, setEditValue] = useState(false);
  const [valueInput, setValueInput] = useState('');
  const [team, setTeam] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [taskForm, setTaskForm] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDue, setTaskDue] = useState('');
  const [taskNote, setTaskNote] = useState('');

  // Memoized derived data
  const sortedSmsHistory = useMemo(
    () => [...(lead?.sms_history || [])].sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || '')),
    [lead?.sms_history],
  );

  const fetchLead = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/leads/${leadId}`, { headers: getAuthHeaders() });
      setLead(data);
      setValueInput(data.proposal_value ? String(data.proposal_value) : '');
      if (!data.read) {
        await axios.patch(`${API}/api/admin/leads/${leadId}/read`, {}, { headers: getAuthHeaders() });
        setLead(prev => ({ ...prev, read: true }));
        onUpdate?.();
      }
    } catch (err) { console.error('[LeadDetail] Load failed:', err?.message); toast.error('Failed to load lead'); }
    finally { setLoading(false); }
  }, [leadId, onUpdate]);

  const fetchTeam = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/team`, { headers: getAuthHeaders() }); setTeam(data); } catch (err) { console.error('[LeadDetail] fetchTeam failed:', err?.message); }
  }, []);

  useEffect(() => {
    fetchLead();
    fetchTeam();
  }, [leadId, fetchLead, fetchTeam]);


  const updateStatus = async (s) => {
    try {
      await axios.patch(`${API}/api/admin/leads/${leadId}/status`, { status: s }, { headers: getAuthHeaders() });
      setLead(prev => ({ ...prev, status: s })); setStatusOpen(false);
      toast.success(`Status: ${getStatusConfig(s).label}`); onUpdate?.();
    } catch { toast.error('Failed to update status'); }
  };

  const saveValue = async () => {
    const val = valueInput ? parseFloat(valueInput) : null;
    try {
      await axios.patch(`${API}/api/admin/leads/${leadId}/value`, { value: val }, { headers: getAuthHeaders() });
      setLead(prev => ({ ...prev, proposal_value: val })); setEditValue(false);
      toast.success('Value updated'); onUpdate?.();
    } catch { toast.error('Failed to update value'); }
  };

  const assignTo = async (name) => {
    try {
      await axios.patch(`${API}/api/admin/leads/${leadId}/assign`, { assigned_to: name || null }, { headers: getAuthHeaders() });
      setLead(prev => ({ ...prev, assigned_to: name || null })); setAssignOpen(false);
      toast.success(name ? `Assigned to ${name}` : 'Unassigned'); onUpdate?.();
    } catch { toast.error('Failed to assign'); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return; setAddingNote(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/leads/${leadId}/notes`, { text: noteText.trim() }, { headers: getAuthHeaders() });
      setLead(prev => ({ ...prev, notes: [...(prev.notes || []), data] })); setNoteText(''); toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
    finally { setAddingNote(false); }
  };

  const deleteNote = async (nid) => {
    try {
      await axios.delete(`${API}/api/admin/leads/${leadId}/notes/${nid}`, { headers: getAuthHeaders() });
      setLead(prev => ({ ...prev, notes: (prev.notes || []).filter(n => n.id !== nid) })); toast.success('Note deleted');
    } catch { toast.error('Failed to delete note'); }
  };

  const addTask = async () => {
    if (!taskTitle.trim()) return;
    try {
      const { data } = await axios.post(`${API}/api/admin/leads/${leadId}/tasks`, { title: taskTitle.trim(), due_date: taskDue || null, note: taskNote || null }, { headers: getAuthHeaders() });
      setLead(prev => ({ ...prev, tasks: [...(prev.tasks || []), data] }));
      setTaskTitle(''); setTaskDue(''); setTaskNote(''); setTaskForm(false); toast.success('Task added'); onUpdate?.();
    } catch { toast.error('Failed to add task'); }
  };

  const toggleTask = async (tid, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await axios.patch(`${API}/api/admin/leads/${leadId}/tasks/${tid}`, { status: newStatus }, { headers: getAuthHeaders() });
      setLead(prev => ({ ...prev, tasks: (prev.tasks || []).map(t => t.id === tid ? { ...t, status: newStatus } : t) })); onUpdate?.();
    } catch { toast.error('Failed to update task'); }
  };

  const deleteTask = async (tid) => {
    try {
      await axios.delete(`${API}/api/admin/leads/${leadId}/tasks/${tid}`, { headers: getAuthHeaders() });
      setLead(prev => ({ ...prev, tasks: (prev.tasks || []).filter(t => t.id !== tid) })); toast.success('Task deleted'); onUpdate?.();
    } catch { toast.error('Failed to delete task'); }
  };

  const followUpAction = async (action) => {
    try {
      const method = action === 'resend' ? 'post' : 'patch';
      await axios[method](`${API}/api/admin/leads/${leadId}/follow-up/${action}`, {}, { headers: getAuthHeaders() });
      toast.success(action === 'booked' ? 'Marked as booked' : action === 'resend' ? 'Follow-up resent' : `Follow-up ${action}d`);
      fetchLead(); onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || `Failed to ${action}`); }
  };

  const [smsText, setSmsText] = useState('');
  const [smsLoading, setSmsLoading] = useState(false);
  const sendSms = async (customMsg) => {
    setSmsLoading(true);
    try {
      const body = customMsg ? { message: customMsg } : {};
      await axios.post(`${API}/api/admin/leads/${leadId}/sms/send`, body, { headers: getAuthHeaders() });
      toast.success('SMS sent');
      fetchLead(); onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'SMS failed'); }
    setSmsLoading(false);
  };

  if (loading) return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"><div className="w-8 h-8 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" /></div>;
  if (!lead) return null;

  const company = lead.company || '', parts = company.split(' | ');
  const org = parts[0] || '';
  const phone = (parts.find(p => p.startsWith('Phone:')) || '').replace('Phone: ', '');
  const interest = (parts.find(p => p.startsWith('Interest:')) || '').replace('Interest: ', '');
  const hot = isHotLead(lead.created_at);
  const statusCfg = getStatusConfig(lead.status || 'new');
  const pendingTasks = (lead.tasks || []).filter(t => t.status === 'pending').sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''));
  const completedTasks = (lead.tasks || []).filter(t => t.status === 'completed');

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-xl h-full bg-slate-950 border-l border-slate-800 overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="lead-detail-panel">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur-md border-b border-slate-800 px-6 py-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-white font-bold text-xl" data-testid="lead-detail-name">{lead.name}</h2>
                {hot && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-500/15 text-orange-400 text-[10px] font-bold uppercase tracking-wider animate-pulse"><Flame className="h-3 w-3" /> HOT</span>}
              </div>
              <p className="text-slate-400 text-sm">{hot ? <span className="text-orange-400">{timeAgo(lead.created_at)}</span> : new Date(lead.created_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="lead-detail-close"><X className="h-5 w-5" /></button>
          </div>
          {/* Status + Value */}
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <div className="relative">
              <button onClick={() => setStatusOpen(!statusOpen)} data-testid="lead-status-selector" className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium ${statusCfg.color}`}>
                {statusCfg.label} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${statusOpen ? 'rotate-180' : ''}`} />
              </button>
              {statusOpen && <div className="absolute top-full left-0 mt-1 bg-slate-900 border border-slate-700 rounded-lg py-1 shadow-xl z-20 min-w-[180px]" data-testid="status-dropdown">
                {STATUSES.map(s => <button key={s.value} onClick={() => updateStatus(s.value)} data-testid={`status-option-${s.value}`} className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-800 ${lead.status === s.value ? 'text-white font-medium' : 'text-slate-300'}`}>{s.label}</button>)}
              </div>}
            </div>
            {/* Proposal Value */}
            {editValue ? (
              <div className="flex items-center gap-1">
                <span className="text-slate-400 text-sm">$</span>
                <Input value={valueInput} onChange={e => setValueInput(e.target.value)} type="number" placeholder="0" data-testid="value-input" className="w-28 h-8 bg-slate-900 border-slate-700 text-white text-sm" />
                <button onClick={saveValue} className="text-emerald-400 hover:text-emerald-300 p-1"><CheckCircle className="h-4 w-4" /></button>
                <button onClick={() => setEditValue(false)} className="text-slate-400 hover:text-white p-1"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <button onClick={() => setEditValue(true)} data-testid="lead-value-display" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-700 text-sm text-slate-300 hover:border-slate-500">
                <DollarSign className="h-3.5 w-3.5 text-emerald-400" />
                {lead.proposal_value ? `$${lead.proposal_value.toLocaleString()}` : 'Add Value'}
              </button>
            )}
          </div>
        </div>

        {/* Engagement Score */}
        {lead.engagement_score && (
          <div className="px-6 py-3 border-b border-slate-800" data-testid="engagement-score-panel">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Engagement Score</h3>
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold ${
                  lead.engagement_score.label === 'hot' ? 'text-red-400' :
                  lead.engagement_score.label === 'warm' ? 'text-amber-400' : 'text-slate-400'
                }`} data-testid="engagement-score-value">{lead.engagement_score.score}</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${
                  lead.engagement_score.label === 'hot' ? 'bg-red-500/15 text-red-400 border-red-500/30' :
                  lead.engagement_score.label === 'warm' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' :
                  'bg-slate-500/15 text-slate-400 border-slate-500/30'
                }`} data-testid="engagement-score-label">{lead.engagement_score.label}</span>
              </div>
            </div>
            {/* Score Bar */}
            <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden mb-2">
              <div
                className={`h-full rounded-full transition-all ${
                  lead.engagement_score.label === 'hot' ? 'bg-gradient-to-r from-red-500 to-orange-500' :
                  lead.engagement_score.label === 'warm' ? 'bg-gradient-to-r from-amber-500 to-yellow-500' :
                  'bg-slate-600'
                }`}
                style={{ width: `${lead.engagement_score.score}%` }}
              />
            </div>
            {/* Breakdown */}
            {lead.engagement_score.breakdown && lead.engagement_score.breakdown.length > 0 && (
              <div className="space-y-1" data-testid="score-breakdown">
                {lead.engagement_score.breakdown.map((item, idx) => (
                  <div key={item.factor || idx} className="flex items-center justify-between text-xs" data-testid={`breakdown-${idx}`}>
                    <span className="text-slate-400">{item.factor}</span>
                    <span className={`font-medium ${item.points > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {item.points > 0 ? '+' : ''}{item.points}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Assignment */}
        <div className="px-6 py-3 border-b border-slate-800 flex items-center justify-between">
          <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Assigned To</span>
          <div className="relative">
            <button onClick={() => setAssignOpen(!assignOpen)} data-testid="lead-assign-selector" className="flex items-center gap-1.5 text-sm px-2 py-1 rounded-lg hover:bg-slate-800">
              <UserCircle className="h-4 w-4 text-slate-400" />
              <span className={lead.assigned_to ? 'text-white' : 'text-slate-500'}>{lead.assigned_to || 'Unassigned'}</span>
              <ChevronDown className="h-3 w-3 text-slate-400" />
            </button>
            {assignOpen && <div className="absolute top-full right-0 mt-1 bg-slate-900 border border-slate-700 rounded-lg py-1 shadow-xl z-20 min-w-[180px]" data-testid="assign-dropdown">
              <button onClick={() => assignTo(null)} className="w-full text-left px-3 py-2 text-sm text-slate-400 hover:bg-slate-800">Unassigned</button>
              {team.map(m => <button key={m.id} onClick={() => assignTo(m.name)} className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-800 ${lead.assigned_to === m.name ? 'text-white font-medium' : 'text-slate-300'}`}>{m.name}</button>)}
            </div>}
          </div>
        </div>

        {/* Speed to Lead */}
        {!lead.first_contact_at && lead.status === 'new' && (
          <div className="px-6 py-3 border-b border-slate-800">
            <Button
              size="sm"
              onClick={async () => {
                try {
                  await axios.patch(`${API}/api/admin/leads/${leadId}/first-contact`, {}, { headers: getAuthHeaders() });
                  toast.success('First contact recorded');
                  fetchLead(); onUpdate?.();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              }}
              data-testid="mark-first-contact"
              className="w-full bg-amber-500/10 border border-amber-500/20 text-amber-400 hover:bg-amber-500/20 text-xs h-8"
            >
              <Zap className="h-3 w-3 mr-1.5" /> Mark First Contact (Speed to Lead)
            </Button>
          </div>
        )}
        {lead.first_contact_at && (
          <div className="px-6 py-2 border-b border-slate-800 flex items-center gap-2">
            <Zap className="h-3.5 w-3.5 text-emerald-400" />
            <span className="text-emerald-400 text-xs font-medium">First contact: {timeAgo(lead.first_contact_at)}</span>
          </div>
        )}

        {/* Contact Info */}
        <div className="px-6 py-4 border-b border-slate-800 space-y-2">
          <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Contact</h3>
          <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-cyan-400 flex-shrink-0" /><a href={`mailto:${lead.email}`} className="text-cyan-400 hover:text-cyan-300 text-sm" data-testid="lead-detail-email">{lead.email}</a><ConsentBadge record={lead} /></div>
          {org && <div className="flex items-center gap-3"><Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" /><span className="text-white text-sm" data-testid="lead-detail-org">{org}</span></div>}
          {phone && <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-slate-400 flex-shrink-0" /><a href={`tel:${phone}`} className="text-white text-sm hover:text-cyan-400" data-testid="lead-detail-phone">{phone}</a></div>}
          {interest && <div className="flex items-center gap-3"><FileText className="h-4 w-4 text-violet-400 flex-shrink-0" /><span className="px-2 py-1 rounded-md bg-violet-500/10 text-violet-400 text-xs font-medium" data-testid="lead-detail-interest">{interest}</span></div>}
        </div>

        {/* Message */}
        {lead.message && <div className="px-6 py-4 border-b border-slate-800"><h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Goals & Challenges</h3><div className="flex gap-3"><MessageSquare className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" /><p className="text-slate-200 text-sm leading-relaxed" data-testid="lead-detail-message">{lead.message}</p></div></div>}

        {/* Tasks */}
        <div className="px-6 py-4 border-b border-slate-800">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Tasks ({pendingTasks.length} pending)</h3>
            <button onClick={() => setTaskForm(!taskForm)} data-testid="add-task-toggle" className="text-cyan-400 hover:text-cyan-300 text-xs font-medium flex items-center gap-1"><Plus className="h-3 w-3" /> Add Task</button>
          </div>
          {taskForm && (
            <div className="p-3 rounded-lg bg-slate-900 border border-slate-800 mb-3 space-y-2" data-testid="task-form">
              <Input value={taskTitle} onChange={e => setTaskTitle(e.target.value)} placeholder="Task title" data-testid="task-title-input" className="bg-slate-950 border-slate-700 text-white text-sm h-9 placeholder:text-slate-500" />
              <div className="flex gap-2">
                <Input type="date" value={taskDue} onChange={e => setTaskDue(e.target.value)} data-testid="task-due-input" className="bg-slate-950 border-slate-700 text-white text-sm h-9 flex-1" />
                <Button onClick={addTask} size="sm" data-testid="save-task-button" className="bg-cyan-500 hover:bg-cyan-400 text-white h-9 px-3"><Send className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          )}
          <div className="space-y-2">
            {pendingTasks.map(t => (
              <div key={t.id} className={`flex items-start gap-2 p-2.5 rounded-lg border group ${isOverdue(t.due_date) ? 'bg-red-500/5 border-red-500/20' : isDueToday(t.due_date) ? 'bg-amber-500/5 border-amber-500/20' : 'bg-slate-900 border-slate-800'}`} data-testid={`task-${t.id}`}>
                <button onClick={() => toggleTask(t.id, t.status)} className="mt-0.5 flex-shrink-0"><div className="w-4 h-4 rounded-full border-2 border-slate-500 hover:border-cyan-400" /></button>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm">{t.title}</p>
                  {t.due_date && <p className={`text-xs mt-0.5 ${isOverdue(t.due_date) ? 'text-red-400 font-medium' : isDueToday(t.due_date) ? 'text-amber-400' : 'text-slate-500'}`}><Calendar className="h-3 w-3 inline mr-1" />{isOverdue(t.due_date) ? 'Overdue' : isDueToday(t.due_date) ? 'Due today' : t.due_date}</p>}
                </div>
                <button onClick={() => deleteTask(t.id)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 p-0.5"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {completedTasks.length > 0 && (
              <details className="mt-2">
                <summary className="text-slate-500 text-xs cursor-pointer hover:text-slate-300">{completedTasks.length} completed</summary>
                <div className="space-y-2 mt-2">
                  {completedTasks.map(t => (
                    <div key={t.id} className="flex items-start gap-2 p-2.5 rounded-lg bg-slate-900/50 border border-slate-800/50 group" data-testid={`task-${t.id}`}>
                      <button onClick={() => toggleTask(t.id, t.status)} className="mt-0.5 flex-shrink-0"><CheckCircle className="h-4 w-4 text-emerald-500" /></button>
                      <p className="text-slate-500 text-sm line-through flex-1">{t.title}</p>
                      <button onClick={() => deleteTask(t.id)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 p-0.5"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              </details>
            )}
            {pendingTasks.length === 0 && completedTasks.length === 0 && <p className="text-slate-500 text-sm text-center py-2">No tasks yet</p>}
          </div>
        </div>

        {/* Follow-Up Sequence */}
        {lead.follow_up && (
          <div className="px-6 py-4 border-t border-slate-800" data-testid="follow-up-section">
            <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
              <MailCheck className="h-3.5 w-3.5" /> Follow-Up Sequence
            </h3>
            <div className="space-y-3">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                  lead.follow_up.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                  lead.follow_up.status === 'booked' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20' :
                  lead.follow_up.status === 'paused' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                  lead.follow_up.status === 'stopped' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                  'bg-slate-500/10 text-slate-400 border border-slate-500/20'
                }`} data-testid="follow-up-status">
                  {lead.follow_up.status === 'booked' ? <CalendarCheck className="h-3 w-3" /> :
                   lead.follow_up.status === 'active' ? <Play className="h-3 w-3" /> :
                   lead.follow_up.status === 'paused' ? <Pause className="h-3 w-3" /> :
                   <Square className="h-3 w-3" />}
                  {lead.follow_up.status?.charAt(0).toUpperCase() + lead.follow_up.status?.slice(1)}
                </span>
                <span className="text-slate-500 text-xs">{lead.follow_up.emails_sent}/3 sent</span>
              </div>

              {/* Progress Dots */}
              <div className="flex items-center gap-2">
                {[1, 2, 3].map(n => {
                  const sent = (lead.follow_up.sequence || []).find(s => s.email_num === n);
                  return (
                    <div key={n} className="flex-1 flex flex-col items-center gap-1">
                      <div className={`w-full h-1.5 rounded-full ${sent ? 'bg-cyan-500' : lead.follow_up.emails_sent >= n ? 'bg-amber-500' : 'bg-slate-800'}`} />
                      <span className="text-[10px] text-slate-500">
                        {n === 1 ? '48h' : n === 2 ? '4 days' : '7 days'}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="p-2 rounded-lg bg-slate-900 border border-slate-800">
                  <p className="text-slate-500 text-[10px] uppercase tracking-wider">Last Sent</p>
                  <p className="text-slate-200 text-xs mt-0.5">{lead.follow_up.last_email_sent ? timeAgo(lead.follow_up.last_email_sent) : 'Not yet'}</p>
                </div>
                <div className="p-2 rounded-lg bg-slate-900 border border-slate-800">
                  <p className="text-slate-500 text-[10px] uppercase tracking-wider">Booking</p>
                  <p className={`text-xs mt-0.5 ${lead.follow_up.booking_completed ? 'text-cyan-400 font-semibold' : 'text-slate-400'}`}>
                    {lead.follow_up.booking_completed ? 'Booked' : 'Not booked'}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                {lead.follow_up.status === 'active' && (
                  <Button size="sm" variant="outline" onClick={() => followUpAction('pause')} data-testid="follow-up-pause" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs h-7">
                    <Pause className="h-3 w-3 mr-1" />Pause
                  </Button>
                )}
                {lead.follow_up.status === 'paused' && (
                  <Button size="sm" variant="outline" onClick={() => followUpAction('resume')} data-testid="follow-up-resume" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs h-7">
                    <Play className="h-3 w-3 mr-1" />Resume
                  </Button>
                )}
                {['active', 'paused'].includes(lead.follow_up.status) && (
                  <Button size="sm" variant="outline" onClick={() => followUpAction('stop')} data-testid="follow-up-stop" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs h-7">
                    <Square className="h-3 w-3 mr-1" />Stop
                  </Button>
                )}
                {!lead.follow_up.booking_completed && (
                  <Button size="sm" variant="outline" onClick={() => followUpAction('booked')} data-testid="follow-up-booked" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 text-xs h-7">
                    <CalendarCheck className="h-3 w-3 mr-1" />Mark Booked
                  </Button>
                )}
                {lead.follow_up.emails_sent < 3 && !lead.follow_up.booking_completed && lead.follow_up.status !== 'stopped' && (
                  <Button size="sm" variant="outline" onClick={() => followUpAction('resend')} data-testid="follow-up-resend" className="border-slate-500/30 text-slate-300 hover:bg-slate-500/10 text-xs h-7">
                    <RefreshCw className="h-3 w-3 mr-1" />Resend
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* SMS Communication */}
        <div className="px-6 py-4 border-b border-slate-800" data-testid="sms-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
              <Smartphone className="h-3.5 w-3.5" /> SMS Communication
            </h3>
            {(lead.sms_history || []).filter(s => s.direction === 'inbound').length > 0 && (
              <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full" data-testid="sms-inbound-count">
                {(lead.sms_history || []).filter(s => s.direction === 'inbound').length} inbound
              </span>
            )}
          </div>

          {/* SMS History */}
          {sortedSmsHistory.length > 0 ? (
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto pr-1" data-testid="sms-history-list">
              {sortedSmsHistory.map((sms, idx) => {
                const isInbound = sms.direction === 'inbound';
                const statusColor = sms.status === 'delivered' ? 'text-emerald-400' :
                  sms.status === 'sent' || sms.status === 'queued' ? 'text-blue-400' :
                  sms.status === 'received' ? 'text-emerald-400' :
                  sms.status === 'failed' || (sms.status && sms.status.startsWith('error')) ? 'text-red-400' :
                  'text-slate-500';
                return (
                  <div
                    key={sms.sid || idx}
                    data-testid={`sms-entry-${idx}`}
                    className={`p-3 rounded-lg border transition-colors ${
                      isInbound
                        ? 'bg-emerald-500/5 border-emerald-500/20 ring-1 ring-emerald-500/10'
                        : 'bg-slate-900 border-slate-800'
                    }`}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                        isInbound ? 'bg-emerald-500/15' : 'bg-blue-500/15'
                      }`}>
                        {isInbound
                          ? <ArrowDown className="h-3 w-3 text-emerald-400" />
                          : <ArrowUp className="h-3 w-3 text-blue-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${isInbound ? 'text-emerald-400' : 'text-blue-400'}`}>
                            {isInbound ? 'Received' : 'Sent'}
                          </span>
                          <span className={`text-[10px] font-medium ${statusColor} bg-slate-800/50 px-1.5 py-0.5 rounded`} data-testid={`sms-status-${idx}`}>
                            {sms.status || 'unknown'}
                          </span>
                        </div>
                        <p className={`text-sm leading-relaxed ${isInbound ? 'text-emerald-100' : 'text-slate-200'}`}>
                          {sms.body}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-slate-500 text-[10px]">{sms.phone}</span>
                          <span className="text-slate-600 text-[10px]">&middot;</span>
                          <span className="text-slate-500 text-[10px]">{sms.timestamp ? timeAgo(sms.timestamp) : '\u2014'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-slate-500 text-sm text-center py-3 mb-3">No SMS history</p>
          )}

          {/* Send SMS */}
          {phone ? (
            <div className="space-y-2" data-testid="sms-send-form">
              <Textarea
                value={smsText}
                onChange={e => setSmsText(e.target.value)}
                placeholder={`Send SMS to ${phone}...`}
                data-testid="sms-text-input"
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 min-h-[56px] text-sm resize-none"
              />
              <div className="flex items-center justify-between">
                <span className="text-slate-500 text-[10px]">{smsText.length}/160 chars</span>
                <Button
                  onClick={() => { sendSms(smsText || null); setSmsText(''); }}
                  disabled={smsLoading}
                  size="sm"
                  data-testid="sms-send-button"
                  className="bg-blue-600 hover:bg-blue-500 text-white text-xs h-8 px-4"
                >
                  <Smartphone className="h-3.5 w-3.5 mr-1.5" />
                  {smsLoading ? 'Sending...' : 'Send SMS'}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-slate-500 text-xs text-center py-1">No phone number on file</p>
          )}
        </div>

        {/* Internal Notes */}
        <div className="px-6 py-4">
          <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3">Notes ({(lead.notes || []).length})</h3>
          <div className="mb-3">
            <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..." data-testid="note-input" className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 min-h-[60px] text-sm" />
            <div className="flex justify-end mt-2"><Button onClick={addNote} disabled={!noteText.trim() || addingNote} size="sm" data-testid="add-note-button" className="bg-cyan-500 hover:bg-cyan-400 text-white"><Send className="h-3.5 w-3.5 mr-1.5" />{addingNote ? 'Adding...' : 'Add Note'}</Button></div>
          </div>
          <div className="space-y-2">
            {(lead.notes || []).slice().reverse().map(note => (
              <div key={note.id} className="p-3 rounded-lg bg-slate-900 border border-slate-800 group" data-testid={`note-${note.id}`}>
                <div className="flex items-start justify-between"><p className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">{note.text}</p><button onClick={() => deleteNote(note.id)} className="opacity-0 group-hover:opacity-100 p-1 text-slate-500 hover:text-red-400 flex-shrink-0 ml-2"><Trash2 className="h-3.5 w-3.5" /></button></div>
                <div className="flex items-center gap-2 mt-2"><span className="text-slate-500 text-xs">{note.author}</span><span className="text-slate-600 text-xs">·</span><span className="text-slate-500 text-xs">{timeAgo(note.created_at)}</span></div>
              </div>
            ))}
            {(lead.notes || []).length === 0 && <p className="text-slate-500 text-sm text-center py-3">No notes yet</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
